import type {ReactElement} from 'react';
import React from 'react';
import {TextStyleOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-styling-options/base-styling-options.types';
import {ThumbnailItemState} from '@Components/thumbnail-item';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ContextualMenu} from '@Components/contextual-menu';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import styles from './style-thumbnail-options.module.scss';

interface StyleThumbnailOptionsProps {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isLineThrough: boolean;
  applyStyle(type: TextStyleOptions): void;
  className?: string;
  isBoldDisabled?: boolean;
}

const ITEM_STYLING_OPTIONS = [TextStyleOptions.BOLD, TextStyleOptions.ITALIC, TextStyleOptions.UNDERLINE, TextStyleOptions.STRIKE_THROUGH];

export function StyleThumbnailOptions({isBoldDisabled = false, className = '', ...props}: StyleThumbnailOptionsProps): ReactElement | null {
  const isSelected = (type: TextStyleOptions): boolean => {
    switch (type) {
      case TextStyleOptions.BOLD:
        return props.isBold;
      case TextStyleOptions.ITALIC:
        return props.isItalic;
      case TextStyleOptions.UNDERLINE:
        return props.isUnderline;
      case TextStyleOptions.STRIKE_THROUGH:
        return props.isLineThrough;
      default:
        return false;
    }
  };

  const getTextFromType = (type: TextStyleOptions): string => {
    switch (type) {
      case TextStyleOptions.BOLD:
        return window.i18next.t('pmwjs_bold');
      case TextStyleOptions.ITALIC:
        return window.i18next.t('pmwjs_italic');
      case TextStyleOptions.UNDERLINE:
        return window.i18next.t('pmwjs_underline');
      case TextStyleOptions.STRIKE_THROUGH:
        return window.i18next.t('pmwjs_strikethrough');
      default:
        throw new Error('unhandled text styling option');
    }
  };

  const getIconFromType = (type: TextStyleOptions): string => {
    switch (type) {
      case TextStyleOptions.BOLD:
        return 'icon-bold';
      case TextStyleOptions.ITALIC:
        return 'icon-italic';
      case TextStyleOptions.UNDERLINE:
        return 'icon-underline';
      case TextStyleOptions.STRIKE_THROUGH:
        return 'icon-strike-through';
      default:
        throw new Error('unhandled text styling option');
    }
  };

  const showBoldDisabledMessageGrowl = (): void => {
    showMessageGrowl({
      text: window.i18next.t('pmwjs_disabled_bold_style_mobile_text'),
      interval: 2000,
      type: GROWL_TYPE.NEUTRAL,
      showCloseIcon: false,
    });
  };

  const getOptions = (): Array<ReactElement> => {
    return ITEM_STYLING_OPTIONS.map((type) => {
      const selected = isSelected(type);
      return (
        <ContextualMenuThumbnailItem
          onClick={(): void => {
            props.applyStyle(type);
          }}
          onClickWhenDisabled={(): void => {
            showBoldDisabledMessageGrowl();
          }}
          isDisabled={type === TextStyleOptions.BOLD && isBoldDisabled}
          key={type}
          className={styles.item}
          thumbnailContainerClassName={selected ? 'content-primary' : ''}
          state={ThumbnailItemState.ICON_ONLY}
          isSelected={selected}
          text={getTextFromType(type)}
          thumbnail={<Icon icon={getIconFromType(type)} size={IconSize.SIZE_ICON_24} />}
        />
      );
    });
  };

  return (
    <ContextualMenu id="style-thumbnail-options" hasMobileModalPadding contentClassName={`flex-h-row spacing-m-0 ${className} ${styles.container}`}>
      {getOptions()}
    </ContextualMenu>
  );
}
